import { apiV2, apiV2Factory } from '../lib/baseAxios';
import { IVFSResponse } from './fileSystemService';

export interface WatchHistoryEvent {
  id: string;
  recordingId: string;
  watchedAt: string;
  createdAt: string;
  shared?: boolean;
  lastWatchedTimestamp?: number;
  updatedAt?: string;
}

export interface WatchHistoryResponse {
  watchHistory: WatchHistoryEvent[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface RecordingWatchInfo {
  hasWatched: boolean;
  lastWatchedAt?: string;
  watchCount: number;
}

export interface WatchStatistics {
  totalWatchEvents: number;
  uniqueRecordings: number;
  mostRecentWatch?: string;
  firstWatch?: string;
}

/**
 * Track when a user watches a video
 */
export const trackWatchEvent = async (
  recordingId: string,
  options?: {
    shared?: boolean;
  },
) => {
  console.log(
    'trackWatchEvent called with recordingId:',
    recordingId,
    'options:',
    options,
  );
  const requestBody = {
    recordingId,
    ...(options?.shared !== undefined && { shared: options.shared }),
  };

  console.log('Making POST request to /watch-history with body:', requestBody);
  try {
    // Use apiV2Factory to get a fresh token instead of the cached apiV2 instance
    const api = apiV2Factory();
    const response = await api.post<IVFSResponse<WatchHistoryEvent>>(
      '/watch-history',
      requestBody,
    );
    console.log('Watch event API response:', response);
    return response.data;
  } catch (error) {
    console.error('Watch event API error:', error);
    throw error;
  }
};

/**
 * Get user's watch history with pagination
 */
export const getUserWatchHistory = async (
  page: number = 1,
  limit: number = 50,
) => {
  const response = await apiV2.get<IVFSResponse<WatchHistoryResponse>>(
    `/watch-history?page=${page}&limit=${limit}`,
  );
  return response.data;
};

/**
 * Check if and when a user watched a specific recording
 */
export const getRecordingWatchInfo = async (recordingId: string) => {
  const response = await apiV2.get<IVFSResponse<RecordingWatchInfo>>(
    `/watch-history/recording/${recordingId}`,
  );
  return response.data;
};

/**
 * Get user's overall watch history statistics
 */
export const getUserWatchStats = async () => {
  const response = await apiV2.get<IVFSResponse<WatchStatistics>>(
    '/watch-history/stats',
  );
  return response.data;
};

/**
 * Update the last watched timestamp for a recording
 */
export const updateWatchTimestamp = async (
  recordingId: string,
  lastWatchedTimestamp: number,
) => {
  const response = await apiV2.put<IVFSResponse<WatchHistoryEvent>>(
    `/watch-history/recording/${recordingId}`,
    { lastWatchedTimestamp },
  );
  return response.data;
};

/**
 * Get recently watched videos (unique recordings only)
 */
export const getRecentlyWatched = async (limit: number = 10) => {
  const response = await apiV2.get<IVFSResponse<WatchHistoryEvent[]>>(
    `/watch-history/recent?limit=${limit}`,
  );
  return response.data;
};

export default {
  trackWatchEvent,
  getUserWatchHistory,
  getRecordingWatchInfo,
  getUserWatchStats,
  updateWatchTimestamp,
  getRecentlyWatched,
};
